import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "src/services/apiClient";
import { Recipient } from "src/types";
import { isBirthDateValid } from "src/helpers/birthDateBounds";
import type { ToastState } from "src/components/Toast/Toast";
import { isDemoMode } from "src/demo/demoApi";
import { formatStreetAndNumber, STREET_WITH_NUMBER_REGEX } from "src/helpers/streetAddress";
import { normalizeSalutationCode } from "src/helpers/salutation";

function recipientFormSyncKey(r: Recipient): string {
    return [
        r.id,
        r.questionnaireSubmittedAt ?? "",
        r.firstName ?? "",
        r.lastName ?? "",
        r.title ?? "",
        r.salutation ?? "",
        r.email ?? "",
        (r.tel ?? []).join(","),
        r.birthDate ? new Date(r.birthDate).toISOString() : "",
        r.street ?? "",
        r.number ?? "",
        r.city ?? "",
        r.plz ?? "",
        r.bankAccountName ?? "",
    ].join("|");
}

type FormFields = {
    firstName: string;
    lastName: string;
    title: string;
    salutation: string;
    email: string;
    tel: string[];
    birthDate: string;
    street: string;
    city: string;
    plz: string;
    bankAccountName: string;
};

function readFormFieldsFromRecipient(r: Recipient): FormFields {
    return {
        firstName: r.firstName ?? "",
        lastName: r.lastName ?? "",
        title: r.title ?? "",
        salutation: normalizeSalutationCode(r.salutation),
        email: r.email ?? "",
        tel: r.tel ?? [],
        birthDate: r.birthDate
            ? new Date(r.birthDate).toISOString().split("T")[0]
            : "",
        street: formatStreetAndNumber(r.street, r.number),
        city: r.city ?? "",
        plz: r.plz ?? "",
        bankAccountName: r.bankAccountName ?? "",
    };
}

function formFieldsDiffer(a: FormFields, b: FormFields): boolean {
    return (
        a.firstName !== b.firstName ||
        a.lastName !== b.lastName ||
        a.title !== b.title ||
        a.salutation !== b.salutation ||
        a.email !== b.email ||
        a.tel.join(",") !== b.tel.join(",") ||
        a.birthDate !== b.birthDate ||
        a.street !== b.street ||
        a.city !== b.city ||
        a.plz !== b.plz ||
        a.bankAccountName !== b.bankAccountName
    );
}

function applyRecipientToForm(
    r: Recipient,
    setters: {
        setFirstName: (v: string) => void;
        setLastName: (v: string) => void;
        setTitle: (v: string) => void;
        setSalutation: (v: string) => void;
        setEmail: (v: string) => void;
        setTel: (v: string[]) => void;
        setBirthDate: (v: string) => void;
        setStreet: (v: string) => void;
        setCity: (v: string) => void;
        setPlz: (v: string) => void;
        setBankAccountName: (v: string) => void;
    },
) {
    const fields = readFormFieldsFromRecipient(r);
    setters.setFirstName(fields.firstName);
    setters.setLastName(fields.lastName);
    setters.setTitle(fields.title);
    setters.setSalutation(fields.salutation);
    setters.setEmail(fields.email);
    setters.setTel(fields.tel);
    setters.setBirthDate(fields.birthDate);
    setters.setStreet(fields.street);
    setters.setCity(fields.city);
    setters.setPlz(fields.plz);
    setters.setBankAccountName(fields.bankAccountName);
}

export function useRecipientForm(
    recipient: Recipient | null,
    id: string,
    onToast: (state: ToastState | null) => void,
    onSaved?: () => void | Promise<void>,
) {
    const { t } = useTranslation();
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [title, setTitle] = useState("");
    const [salutation, setSalutation] = useState("H");
    const [email, setEmail] = useState("");
    const [tel, setTel] = useState<string[]>([]);
    const [birthDate, setBirthDate] = useState("");
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");
    const [plz, setPlz] = useState("");
    const [bankAccountName, setBankAccountName] = useState("");
    const [lastSyncedKey, setLastSyncedKey] = useState("");
    const syncedFieldsRef = useRef<FormFields | null>(null);
    const syncedRecipientIdRef = useRef<string | null>(null);
    const lastQuestionnaireSubmittedAtRef = useRef<string | null>(null);
    const [saving, setSaving] = useState(false);

    const setters = useMemo(
        () => ({
            setFirstName,
            setLastName,
            setTitle,
            setSalutation,
            setEmail,
            setTel,
            setBirthDate,
            setStreet,
            setCity,
            setPlz,
            setBankAccountName,
        }),
        [],
    );

    const currentFields = useMemo<FormFields>(
        () => ({
            firstName,
            lastName,
            title,
            salutation,
            email,
            tel,
            birthDate,
            street,
            city,
            plz,
            bankAccountName,
        }),
        [
            firstName,
            lastName,
            title,
            salutation,
            email,
            tel,
            birthDate,
            street,
            city,
            plz,
            bankAccountName,
        ],
    );

    const currentFieldsRef = useRef(currentFields);
    currentFieldsRef.current = currentFields;

    /** True only when the user changed something since the last server sync (not when server moved ahead). */
    const isDirty = useMemo(() => {
        const base = syncedFieldsRef.current;
        if (!base) return false;
        return formFieldsDiffer(currentFields, base);
    }, [currentFields]);

    const syncKey = recipient ? recipientFormSyncKey(recipient) : "";

    useEffect(() => {
        if (!recipient?.id) {
            syncedFieldsRef.current = null;
            syncedRecipientIdRef.current = null;
            setLastSyncedKey("");
            return;
        }
        if (syncedRecipientIdRef.current && syncedRecipientIdRef.current !== recipient.id) {
            syncedFieldsRef.current = null;
            syncedRecipientIdRef.current = null;
            lastQuestionnaireSubmittedAtRef.current = null;
            setLastSyncedKey("");
        }
    }, [recipient?.id]);

    /** Questionnaire submit always wins — push merged server data into the form. */
    useEffect(() => {
        if (!recipient) return;
        const qAt = recipient.questionnaireSubmittedAt ?? "";
        if (!qAt || qAt === lastQuestionnaireSubmittedAtRef.current) return;
        lastQuestionnaireSubmittedAtRef.current = qAt;
        applyRecipientToForm(recipient, setters);
        syncedFieldsRef.current = readFormFieldsFromRecipient(recipient);
        syncedRecipientIdRef.current = recipient.id;
        setLastSyncedKey(recipientFormSyncKey(recipient));
    }, [recipient, setters]);

    useEffect(() => {
        if (!recipient || !syncKey || syncKey === lastSyncedKey) return;
        if (
            syncedFieldsRef.current &&
            formFieldsDiffer(currentFieldsRef.current, syncedFieldsRef.current)
        ) {
            return;
        }
        applyRecipientToForm(recipient, setters);
        syncedFieldsRef.current = readFormFieldsFromRecipient(recipient);
        syncedRecipientIdRef.current = recipient.id;
        setLastSyncedKey(syncKey);
    }, [recipient, syncKey, lastSyncedKey, setters]);

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const handleSave = useCallback(async () => {
        if (!lastName || !salutation) {
            onToast({
                message: t("recipientPage.validationError"),
                variant: "error",
            });
            return;
        }
        if (email && !EMAIL_RE.test(email.trim())) {
            onToast({ message: t("form.emailInvalid"), variant: "error" });
            return;
        }
        if (birthDate && !isBirthDateValid(birthDate)) {
            onToast({ message: t("form.birthDateInvalid"), variant: "error" });
            return;
        }
        if (street && !STREET_WITH_NUMBER_REGEX.test(street.trim())) {
            onToast({ message: t("form.streetError"), variant: "error" });
            return;
        }
        setSaving(true);
        try {
            await apiRequest(
                `/recipients/${id}`,
                {
                    method: "PATCH",
                    body: JSON.stringify({
                        firstName,
                        lastName,
                        title,
                        salutation,
                        email,
                        tel: tel.filter(Boolean),
                        birthDate: birthDate || undefined,
                        street,
                        city,
                        plz,
                        bankAccountName: bankAccountName || undefined,
                    }),
                },
                { "Content-Type": "application/json" },
            );
            onToast({
                message: isDemoMode() ? t("common.demoSimulated") : t("common.changesSaved"),
                variant: "success",
            });
            if (recipient) {
                syncedFieldsRef.current = {
                    ...currentFields,
                    tel: tel.filter(Boolean),
                };
            }
            await onSaved?.();
        } catch {
            onToast({ message: t("common.saveFailed"), variant: "error" });
        } finally {
            setSaving(false);
        }
    }, [
        id,
        firstName,
        lastName,
        title,
        salutation,
        email,
        tel,
        birthDate,
        street,
        city,
        plz,
        bankAccountName,
        recipient,
        currentFields,
        tel,
        onToast,
        onSaved,
        t,
    ]);

    const initialized = !!recipient && lastSyncedKey === syncKey;

    return {
        firstName,
        setFirstName,
        lastName,
        setLastName,
        title,
        setTitle,
        salutation,
        setSalutation,
        email,
        setEmail,
        tel,
        setTel,
        birthDate,
        setBirthDate,
        street,
        setStreet,
        city,
        setCity,
        plz,
        setPlz,
        bankAccountName,
        setBankAccountName,
        isDirty,
        saving,
        handleSave,
        initialized,
    };
}
